import React, { useCallback, useContext, useState } from 'react';
import { HELP_DESK_PRIORITIES, HELP_DESK_STATUSES } from '@ubeya/shared/constants/helpDesk';
import styled, { css } from 'styled-components';
import { Avatar } from '@ubeya/shared-web/components/Avatar';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { ago } from '@ubeya/shared/utils/time';
import useBranches from '@ubeya/shared/hooks/account/useBranches';
import { ReactComponent as BaseSnoozeIcon } from '@ubeya/shared-web/assets/help-desk/snooze.svg';
import { useHelpDeskAnalytics } from '../../helpDeskAnalytics';
import { getMessagePreviewContent, getSnoozedLabel, getTicketBranch } from '../../helpDeskUtils';
import ResolvedBadge from './ResolvedBadge';
import ClosedBadge from './ClosedBadge';
import CategoryDisplay from './CategoryDisplay';
import AssigneeDisplay from './AssigneeDisplay';
import BranchDisplay from './BranchDisplay';
import PriorityDisplay from './PriorityDisplay';
import UnreadBadge from './UnreadBadge';
import MetaRow from './MetaRow';
import MetaChip, { chipsRowStyle } from './MetaChip';
import EventLink from './EventLink';
import TicketListItemActions from './TicketListItemActions';
import TicketBadge from './TicketBadge';

const ActionsTrigger = styled(TicketListItemActions)`
  display: ${({ $forceVisible }) => ($forceVisible ? 'flex' : 'none')};
  margin-inline-start: auto;
  flex-shrink: 0;
`;

const TicketMetaRow = styled(MetaRow)`
  ${chipsRowStyle}
`;

const TicketRow = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px;
  cursor: pointer;
  background: ${({ theme, $selected }) => ($selected ? theme.colors.grey1 : 'transparent')};
  border-radius: 8px;

  ${({ $selected }) =>
    !$selected &&
    css`
      &:hover {
        background: ${({ theme }) => theme.colors.lightHoverBackground};

        ${TicketMetaRow} > * {
          background: transparent;
        }
      }
    `}

  &:hover ${ActionsTrigger} {
    display: flex;
  }

  gap: 8px;
  margin-bottom: 16px;
`;

const MainRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
`;

const AvatarWrap = styled.div`
  flex-shrink: 0;
  padding-top: 2px;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
`;

const LineRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  min-height: 24px;
`;

const EmployeeName = styled.span`
  font-size: 16px;
  line-height: 120%;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const Title = styled.div`
  font-size: 14px;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.black};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 500;
  min-width: 0;
  flex: 1;
`;

const TicketNumber = styled.div`
  font-size: 14px;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.grey4};
  white-space: nowrap;
  flex-shrink: 0;
`;

const Preview = styled.span`
  font-size: 14px;
  line-height: 20px;
  color: ${({ theme }) => theme.colors.grey4};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
  min-width: 0;
  flex: 1;
`;

const Time = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey4};
  white-space: nowrap;
  flex-shrink: 0;
`;

const SnoozeInfo = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  align-self: flex-end;
  color: ${({ theme }) => theme.colors.black};
`;

const SnoozeIcon = styled(BaseSnoozeIcon)`
  width: 20px;
  height: 20px;
  flex-shrink: 0;

  circle,
  path,
  line {
    stroke: ${({ theme }) => theme.colors.grey4};
  }
`;

const SnoozeText = styled.div`
  min-width: 0;
  font-size: 12px;
  line-height: 20px;
  white-space: nowrap;
`;

const getLastMessagePreview = ({ lastMessage, fallbackPreview, t }) => {
  const rawPreview = getMessagePreviewContent(lastMessage, t).trim();
  const contentPreview =
    rawPreview && lastMessage?.isAutomated === true ? t(rawPreview, { defaultValue: rawPreview }) : rawPreview;
  const hasAttachments = lastMessage?.hasAttachments || lastMessage?.attachments?.length > 0;
  const previewBody = contentPreview || (hasAttachments ? t('helpDeskAttachmentPreview') : '');
  const senderFirstName =
    lastMessage?.isAutomated === true ? t('automated_message') : lastMessage?.senderName?.trim().split(/\s+/)[0];

  if (previewBody) {
    return [senderFirstName, previewBody].filter(Boolean).join(': ');
  }

  return fallbackPreview || null;
};

const TicketListItem = ({ item, selected = false, onClick, accountId }) => {
  const { t } = useContext(ConfigContext);
  const { mappedBranches } = useBranches();
  const { trackHelpDeskTicketEvent } = useHelpDeskAnalytics();
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);
  const ticket = item.ticket ?? item;
  const emp = item.employee || ticket.employee || {};
  const empName =
    item.employeeName || ticket.employeeName || [emp.firstName, emp.lastName].filter(Boolean).join(' ') || '';
  const empParts = empName.split(' ');
  const empAvatar = item.employeeAvatarUrl || ticket.employeeAvatarUrl || emp.image;
  const isResolved = !!ticket.resolvedAt || !!ticket.isResolved;
  const isClosed = ticket.status === HELP_DESK_STATUSES.CLOSED;

  const lastMsg = item.lastMessage;
  const lastPreview = getLastMessagePreview({ lastMessage: lastMsg, fallbackPreview: item.lastMessagePreview, t });
  const dateForAgo = lastMsg?.createdAt || ticket.updatedAt;
  const timeAgo = dateForAgo ? ago(dateForAgo, t) : '';

  const catObj = item.category || ticket.category;
  const categoryName = catObj?.name || item.categoryName || ticket.categoryName;
  const category = categoryName ? { name: categoryName, icon: catObj?.icon, color: catObj?.color } : null;

  const branch = getTicketBranch({ branch: item.branch || ticket.branch, mappedBranches });

  const assigneeName = item.assigneeName || ticket.assigneeName;
  const assigneeAvatar = item.assigneeAvatarUrl || ticket.assigneeAvatarUrl;
  const admin = assigneeName ? { name: assigneeName, image: assigneeAvatar } : null;

  const priority = ticket.priority;
  const unreadCount = item.unreadCount || 0;
  const isManuallyUnread = !!item.isManuallyUnread;
  const snoozeLabel =
    ticket.status === HELP_DESK_STATUSES.SNOOZED
      ? getSnoozedLabel(ticket.snoozedUntil, t, { relativeOnly: true })
      : null;
  const relatedShiftId = ticket.shiftId || item.shiftId;

  const handleClick = useCallback(() => {
    if (!selected) {
      trackHelpDeskTicketEvent({
        eventName: 'Help Desk - Admin Opened Ticket',
        context: 'Help Desk ticket list',
        ticket,
        ticketStatus: ticket.status,
        eventProperties: {
          employeeId: emp.id,
          isResolved,
          categoryId: catObj?.id,
          priority,
          relatedShiftId
        }
      });
    }

    onClick?.();
  }, [catObj?.id, emp.id, isResolved, onClick, priority, relatedShiftId, selected, ticket, trackHelpDeskTicketEvent]);

  return (
    <TicketRow $selected={selected} onClick={handleClick}>
      <MainRow>
        <AvatarWrap>
          <Avatar
            firstName={empParts[0] || ''}
            lastName={empParts.slice(1).join(' ') || ''}
            image={empAvatar}
            size={40}
          />
        </AvatarWrap>
        <Content>
          {/* Line 1: Employee name + status badges + actions menu */}
          <HeaderRow>
            <EmployeeName>{empName || '—'}</EmployeeName>
            <TicketBadge ticket={ticket} />
            {isResolved && <ResolvedBadge />}
            {isClosed && <ClosedBadge />}
            {accountId && (
              <ActionsTrigger
                ticket={ticket}
                accountId={accountId}
                unreadCount={unreadCount}
                isManuallyUnread={isManuallyUnread}
                onOpenChange={setIsActionsMenuOpen}
                $forceVisible={isActionsMenuOpen}
              />
            )}
          </HeaderRow>

          {/* Line 2: Title + #ticketNumber */}
          <LineRow>
            <Title dir="auto">{ticket.title || ''}</Title>
            <TicketNumber>#{ticket.ticketNumber || ticket.id || item.id}</TicketNumber>
          </LineRow>

          {/* Line 3: Last message preview + time ago + unread */}
          <LineRow>
            <Preview dir="auto">{lastPreview || ''}</Preview>
            {timeAgo && <Time>{timeAgo}</Time>}
            <UnreadBadge count={unreadCount} isDot={isManuallyUnread} />
          </LineRow>
        </Content>
      </MainRow>

      {/* Line 4: Branch, category, assignee, priority and related event chips */}
      <TicketMetaRow $noMargin>
        {branch && (
          <MetaChip $roundContent>
            <BranchDisplay branch={branch} />
          </MetaChip>
        )}

        {category && (
          <MetaChip>
            <CategoryDisplay category={category} />
          </MetaChip>
        )}

        {admin && (
          <MetaChip $roundContent>
            <AssigneeDisplay admin={admin} hideNoAssigneeIcon />
          </MetaChip>
        )}

        {priority && priority !== HELP_DESK_PRIORITIES.NONE && (
          <MetaChip>
            <PriorityDisplay priority={priority} />
          </MetaChip>
        )}

        <EventLink ticket={ticket} accountId={accountId} disableLink />
      </TicketMetaRow>

      {/* Line 5: Snooze countdown, on its own row below the chips */}
      {snoozeLabel && (
        <SnoozeInfo>
          <SnoozeIcon />
          <SnoozeText>{snoozeLabel}</SnoozeText>
        </SnoozeInfo>
      )}
    </TicketRow>
  );
};

export default TicketListItem;
